import React from "react";
// Components
import { TableBody, TableHead } from "./components";
// Hooks
import useActions from "hooks/useActions";
import usePermissions from "hooks/usePermissions";
// Store Utils
import { observationsSortingAction } from "store/filters";
import { useDispatch, useSelector } from "react-redux";
// Utils
import getAllRowsData from "utils/getAllRowsData";
// Exteranl Tools
import { snakeCase } from "change-case";
// Styling
import "./Observations.scss";
// types
import { TableProps } from "./types";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";
import { filterKeys } from "Components/Users/Table";
import { openBulkArchiveObservationModalAction } from "store/modals/reducer.actions";
import { RootState } from "store";
import { filtersInitialState } from "store/filters/initialState";
import { useLocation, useParams } from "react-router-dom";
import { generateQueryStringForUsers } from "utils/convertFiltersToQueryString";
import {
  applyObservationListScopeToQuery,
  buildObservationNavigationContext,
} from "utils/observationNavigation";
import { IfetchObservations } from "store/observations/initialState";

export const KEYS_TO_BE_REMOVED = ["id"];

const generatingDirectoryActions = (
  columns: any,
  rowsData: any,
  openBulkArchiveModal: () => void,
  showArchiveAll: boolean
) => {
  const fileDownload = async () => {
    if (rowsData.length === 0) {
      Toasts.error("Please select at least one observation from the table");
      return;
    }

    try {
      const res = await apiLibrary.file.fileDownload(
        "observations",
        columns,
        rowsData?.map((item: any) => {
          return item?.observationCode?.id;
        })
      );

      // Convert the response to a Blob
      const blob = new Blob([res], { type: "application/octet-stream" });

      // Create a link element
      const link = document.createElement("a");

      // Set the href attribute to a URL created from the Blob
      link.href = window.URL.createObjectURL(blob);

      // Set the download attribute to specify the file name
      link.download = `observations_${new Date().toISOString()}.csv`;

      // Append the link to the body
      document.body.appendChild(link);

      // Programmatically click the link to trigger the download
      link.click();

      // Remove the link from the DOM
      document.body.removeChild(link);

      Toasts.success("Download complete");
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error?.message;
      Toasts.error(errorMsg);
    }
  };

  return [
    {
      label: "Export Details",
      onClick: () => fileDownload(),
      divider: false,
      isVisible: true,
    },
    {
      label: "Archive All",
      onClick: () => openBulkArchiveModal(),
      divider: false,
      isVisible: showArchiveAll,
    },
  ];
};

export const Table: React.FC<TableProps> = ({
  table,
  columnOrder,
  columns,
  columnVisibility,
  flexRender,
  setSorting,
  sorting,
}) => {
  const dispatch = useDispatch();
  const { observationsActions } = useActions();
  const { observations } = usePermissions();
  const { observationsFilter } = useSelector<RootState, filtersInitialState>(
    (state) => state.Filters
  );
  const { data } = useSelector<RootState, IfetchObservations>(
    (state) => state.allObservations
  );
  const { communityId, programId } = useParams();
  const location = useLocation();

  const canArchiveObservation = (observation: any) =>
    (observation?.status === "in_review" ||
      observation?.status === "completed") &&
    observations.canArchiveObservation &&
    observation?.programStatus !== "completed";

  const selectedRows = table?.getSelectedRowModel()?.rows ?? [];
  const selectedRowsCount = selectedRows.length;
  const selectedObservations = selectedRows.map((row: any) => row?.original);
  const selectedObservationIds = selectedObservations
    .filter((observation: any) => canArchiveObservation(observation))
    .map((observation: any) => observation?.observationCode?.id);
  const hasInvalidSelection = selectedObservationIds.length !== selectedRowsCount;
  const canShowArchiveAll =
    (observationsFilter.status === "in_review" ||
      observationsFilter.status === "completed" ||
      observationsFilter.status === "all") &&
    observations.canArchiveObservation;

  const openBulkArchiveModal = () => {
    if (selectedRowsCount === 0) {
      Toasts.error("Please select at least one observation from the table");
      return;
    }

    if (hasInvalidSelection) {
      Toasts.error(
        "Please select only in review or completed observations from programs that are not completed."
      );
      return;
    }

    dispatch(
      openBulkArchiveObservationModalAction({
        ids: selectedObservationIds,
        observations: selectedObservations,
        path: "directory",
      })
    );
  };

  const DirectoryActions = () => {
    const filteredKeys = filterKeys(columnVisibility, columnOrder);
    const rowsData = getAllRowsData(table, columnOrder, columnVisibility, columns);

    return generatingDirectoryActions(
      filteredKeys,
      rowsData,
      openBulkArchiveModal,
      canShowArchiveAll
    );
  };

  const getObservationsActions = (data: any) => {
    const modalData = {
      status: data?.status,
      id: data?.id,
      submissionDate: data?.submissionDate,
      protocolId: data.protocolId,
      path: "directory",
      programStatus: data?.programStatus,
    };

    return observationsActions(modalData);
  };

  const toggleSorting = (columnId: string) => {
    setSorting((currentSorting: any) => {
      const existingSort = currentSorting.find(
        (sort: any) => sort.columnId === columnId
      );
      const columnsId = snakeCase(columnId);
      if (!existingSort) {
        dispatch(observationsSortingAction(columnsId, "asc"));

        return [{ columnId, type: "asc" }];
      } else if (existingSort.type === "asc") {
        dispatch(observationsSortingAction(columnsId, "desc"));

        return [{ columnId, type: "desc" }];
      } else {
        dispatch(observationsSortingAction("", ""));

        return [];
      }
    });
  };
  const handleClickOnTableColumnHeading = (header: any) =>
    header.column.getCanSort() && toggleSorting(header.column.id);
  const queryParams = applyObservationListScopeToQuery(
    generateQueryStringForUsers(observationsFilter),
    {
      communityId,
      programId,
      kpiState: location.state,
    }
  );
  const observationNavigationContext = buildObservationNavigationContext(
    table.getRowModel().rows.map((row: any) => row.original),
    window.location.pathname,
    {
      source: "observations",
      queryParams,
      totalPages: data?.totalPages,
    }
  );

  return (
    <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
      <table className="w-full">
        <TableHead
          table={table}
          sorting={sorting}
          flexRender={flexRender}
          DirectoryActions={DirectoryActions()}
          handleClickOnColumnHead={handleClickOnTableColumnHeading}
        />
        <TableBody
          flexRender={flexRender}
          table={table}
          columnVisibility={columnVisibility}
          getObservationActions={getObservationsActions}
          observationNavigationContext={observationNavigationContext}
        />
      </table>
    </div>
  );
};
